import { useEffect } from 'react';
import { Modal, TextInput, Textarea, Button, Group, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { frameworksApi, type Framework } from '../../../services/api/frameworks';
import { getApiErrorMessage } from '../../../services/api/client';

// AC4 — field guidance shown below each input so the description gets written in
// a way the AI can use. Copy is taken verbatim from the ticket's suggestions.
const NAME_HELP = 'The framework or acronym, spelled exactly as it should appear.';
const DESCRIPTION_HELP =
  "Context to help the AI recognize and spell it correctly, e.g. what the acronym stands for or who it's named after.";

interface FrameworkFormModalProps {
  opened: boolean;
  onClose: () => void;
  /** When provided, the modal is in edit mode (AC3); otherwise create mode (AC2). */
  framework?: Framework | null;
  /** Existing framework names (lower-cased) for instant client-side dedup (AC6). */
  existingNames: string[];
  onSaved: () => void;
}

export function FrameworkFormModal({
  opened,
  onClose,
  framework,
  existingNames,
  onSaved,
}: FrameworkFormModalProps) {
  const isEdit = Boolean(framework);

  const form = useForm({
    initialValues: {
      name: '',
      description: '',
    },
    validate: {
      // AC6 — name required + no case-insensitive duplicates. The DB and API
      // enforce this authoritatively; this gives instant feedback before submit.
      name: (value) => {
        const trimmed = value.trim();
        if (!trimmed) return 'Name is required';
        const collidesWith = existingNames.includes(trimmed.toLowerCase());
        // In edit mode, the framework's own current name is not a duplicate.
        const isOwnName =
          isEdit && framework?.name.trim().toLowerCase() === trimmed.toLowerCase();
        if (collidesWith && !isOwnName) return 'A framework with this name already exists';
        return null;
      },
    },
  });

  // Reset the form to the target framework (or blank) whenever the modal opens.
  useEffect(() => {
    if (opened) {
      form.setValues({
        name: framework?.name ?? '',
        description: framework?.description ?? '',
      });
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, framework]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = async (values: typeof form.values) => {
    const payload = {
      name: values.name.trim(),
      description: values.description.trim() || null,
    };

    try {
      if (isEdit && framework) {
        await frameworksApi.update(framework.id, payload);
      } else {
        await frameworksApi.create(payload);
      }
      onSaved();
      handleClose();
    } catch (error) {
      // AC6 — a duplicate that slipped past the client check (e.g. a concurrent
      // add) returns 409; surface it inline on the name field.
      const status = (error as { status?: number }).status;
      if (status === 409) {
        form.setFieldError('name', 'A framework with this name already exists');
        return;
      }
      notifications.show({
        title: 'Error',
        message: getApiErrorMessage(error, 'Failed to save framework'),
        color: 'red',
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEdit ? 'Edit Framework' : 'Add Framework'}
      size="lg"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Name"
            description={NAME_HELP}
            placeholder="e.g. LADA"
            required
            data-autofocus
            {...form.getInputProps('name')}
          />

          <Textarea
            label="Description"
            description={DESCRIPTION_HELP}
            placeholder="e.g. Stands for Listen, Ask, Decide, Act — a coaching conversation model."
            autosize
            minRows={3}
            {...form.getInputProps('description')}
          />

          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit">{isEdit ? 'Save Changes' : 'Add Framework'}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export default FrameworkFormModal;
